import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getToken, setToken, removeToken } from '../../utils/auth'
import { isTokenExpired } from '../../utils/tokenCheck'
import type { User, AuthState } from '../../features/auth/types'
import { apiGet, apiPost, parseApiResponse } from '../../api/instance'
import { API_ENDPOINTS } from '../../api/endpoints'

const initialState: AuthState = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
}

/**
 * Check authentication status by validating token and fetching user data
 */
export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    const token = getToken()

    // If no token or token is expired, user is not authenticated
    if (!token || isTokenExpired(token)) {
      removeToken()
      return rejectWithValue('No valid token')
    }

    try {
      const response = await apiGet(API_ENDPOINTS.AUTH.ME)

      if (response.ok) {
        const result = await parseApiResponse<{
          id: number
          email: string
          name: string
          role: string
        }>(response)

        if (result.success && result.data) {
          const userData = result.data
          // Map 'user' role from backend to 'viewer' if needed, or use as-is
          const userRole = userData.role?.toLowerCase() || userData.role
          const mappedRole = userRole === 'user' ? 'viewer' : userRole
          return {
            id: userData.id,
            email: userData.email,
            name: userData.name,
            role: mappedRole as 'admin' | 'underwriter' | 'viewer',
          } as User
        }
        removeToken()
        return rejectWithValue('No user data')
      }
      // 401 or other error - user is not authenticated
      removeToken()
      return rejectWithValue('Authentication failed')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Auth check error:', error)
      removeToken()
      return rejectWithValue('Network error')
    }
  }
)

/**
 * Login action - sets user data and token
 */
export const login = createAsyncThunk(
  'auth/login',
  async (
    { userData, token }: { userData: User; token?: string },
    { dispatch }
  ) => {
    if (token) {
      setToken(token)
    }
    // After login, check auth to ensure state is properly set
    await dispatch(checkAuth())
    return userData
  }
)

/**
 * Logout action - clears user data and token
 */
export const logout = createAsyncThunk('auth/logout', async () => {
  try {
    await apiPost(API_ENDPOINTS.AUTH.LOGOUT)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Logout error:', error)
  } finally {
    removeToken()
  }
  return null
})

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Clear user if token becomes invalid
    clearUserIfTokenInvalid: state => {
      const token = getToken()
      const hasToken = !!token
      const tokenExpired = token ? isTokenExpired(token) : true
      const tokenInvalid = !hasToken || tokenExpired

      if (tokenInvalid) {
        state.user = null
        state.isAuthenticated = false
        if (token) {
          removeToken()
        }
      }
    },
    // Update user data (for profile updates)
    updateUser: (state, action: { payload: Partial<User> }) => {
      if (state.user) {
        state.user = {
          ...state.user,
          ...action.payload,
        }
      }
    },
  },
  extraReducers: builder => {
    // Check auth
    builder
      .addCase(checkAuth.pending, state => {
        state.isLoading = true
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload
        state.isAuthenticated = true
      })
      .addCase(checkAuth.rejected, state => {
        state.isLoading = false
        state.user = null
        state.isAuthenticated = false
      })

    // Login
    builder.addCase(login.fulfilled, (state, action) => {
      state.user = action.payload
      state.isAuthenticated = true
    })

    // Logout
    builder.addCase(logout.fulfilled, state => {
      state.user = null
      state.isAuthenticated = false
    })
  },
})

export const { clearUserIfTokenInvalid, updateUser } = authSlice.actions
export default authSlice.reducer
